import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import { AlertCircle, DollarSign, Package, Wrench, Settings, ArrowRight, X } from 'lucide-react'
import api from '../services/api'
import DNAProgressEngine from './DNAProgressEngine'
import { useDNAIntegrityStore } from '../stores/dnaIntegrityStore'

const SECTION_LABELS = {
  financials: { label: 'Financiën', icon: DollarSign, link: '/settings/dna' },
  materials: { label: 'Materialen', icon: Package, link: '/materialen' },
  hardware: { label: 'Hardware', icon: Wrench, link: '/master-dna?tab=hardware' },
  machines: { label: 'Machine-instellingen', icon: Settings, link: '/settings/machines' },
}

/**
 * DNA Integrity Banner
 * Transforms ConfigurationError (503) into a helpful guide.
 * Shows proactively when DNA is incomplete, or reactively when pricing fails.
 * Professional SaaS-style integrity check (HubSpot/Salesforce pattern).
 */
export default function DNAIntegrityBanner() {
  const navigate = useNavigate()
  const location = useLocation()
  const { configurationError, clearConfigurationError, dnaJustSaved } = useDNAIntegrityStore()

  const { data: dnaStatus, isError: dnaStatusError } = useQuery({
    queryKey: ['dna-status'],
    queryFn: async () => {
      const response = await api.get('/setup/dna/status')
      return response.data
    },
    refetchInterval: 60000,
    retry: false,
  })

  // Clear ConfigurationError when DNA is now complete (user fixed it)
  useEffect(() => {
    if (dnaStatus?.is_complete && configurationError) {
      clearConfigurationError()
    }
  }, [dnaStatus?.is_complete, configurationError, clearConfigurationError])

  // Don't show when status failed to load (avoid false "incomplete" state)
  if (dnaStatusError) return null

  // Don't show on DNA/setup pages - user is already there
  const isOnSetupPage =
    location.pathname.startsWith('/settings/dna') ||
    location.pathname.startsWith('/setup') ||
    location.pathname.startsWith('/master-dna')

  if (isOnSetupPage || dnaJustSaved) {
    return null
  }

  const incompleteSections =
    configurationError?.incomplete_sections ?? dnaStatus?.incomplete_sections ?? []
  const isIncomplete = !dnaStatus?.is_complete && incompleteSections.length > 0
  const showFromError = configurationError && !isIncomplete

  if (!isIncomplete && !showFromError) {
    return null
  }

  const message =
    configurationError?.detail ??
    'Vul eerst je Bedrijfs-DNA in via Setup of Instellingen voordat je prijzen kunt berekenen.'
  const sections = incompleteSections.length
    ? incompleteSections
    : ['financials']

  return (
    <div
      role="alert"
      className="mb-6 rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-5 shadow-sm dark:border-blue-800 dark:from-blue-950/40 dark:to-indigo-950/40"
    >
      {/* DNA Progress Engine: balk + sectie-iconen (Euro, Zaag, Klok) */}
      <div className="mb-4">
        <DNAProgressEngine compact onSectionClick={(s) => { clearConfigurationError(); navigate(s.link) }} />
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <AlertCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-blue-900 dark:text-blue-100">
            {showFromError ? 'Prijzen niet beschikbaar' : 'Bedrijfs-DNA onvolledig'}
          </h3>
          <p className="mt-1 text-sm text-blue-800 dark:text-blue-200">{message}</p>

          <div className="mt-4 space-y-2">
            {sections.map((section) => {
              const config = SECTION_LABELS[section]
              if (!config) return null
              const Icon = config.icon
              return (
                <button
                  key={section}
                  onClick={() => {
                    clearConfigurationError()
                    navigate(config.link)
                  }}
                  className="flex w-full items-center justify-between rounded-lg border border-blue-200 bg-white px-4 py-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-blue-700 dark:bg-blue-950/30 dark:hover:border-blue-600 dark:hover:bg-blue-950/50"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Stel je {config.label.toLowerCase()} in
                    </span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                </button>
              )
            })}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-blue-200 pt-4 dark:border-blue-800">
            <button
              onClick={() => {
                clearConfigurationError()
                navigate('/settings/dna')
              }}
              className="text-sm font-medium text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100"
            >
              Open Bedrijfs-DNA instellingen
              <ArrowRight className="ml-1 inline h-4 w-4" />
            </button>
            <button
              onClick={() => {
                clearConfigurationError()
                navigate('/setup')
              }}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
            >
              Start Setup-wizard
            </button>
          </div>
        </div>
        {configurationError && (
          <button
            onClick={clearConfigurationError}
            className="flex-shrink-0 rounded p-1 text-gray-500 hover:bg-blue-100 hover:text-gray-700 dark:hover:bg-blue-900/50"
            aria-label="Sluiten"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  )
}
